import React from 'react';
import { common } from '@kurtosys/ksys-app-template';
import { IAppContext } from '@kurtosys/types/helpers/AnalyticsHelper/IAppContext.js';
import { action, computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import { AnalyticsContext, ComponentBase, InjectedStyledComponent, KEYS } from '../../../shared/index.js';
import { ITranslationContextValue } from '../../base/Translate/models/ITranslationContextValue.js';
import Translate, { TranslationContext } from '../../base/Translate/Translate.js';

import { IChartPeriodSelectionResponse } from './models/IChartPeriodSelectionResponse.js';
import {
	IChartPeriodProps,
	IChartPeriodSelectionConfiguration,
	IChartPeriodSelectionProps,
	IChartPeriodSelectionState,
	TPeriods,
} from './models/index.js';
import ActiveChartPeriod from './styledComponents/ActiveChartPeriod.js';
import DisabledChartPeriod from './styledComponents/DisabledChartPeriod.js';
import Wrapper from './styledComponents/Wrapper.js';
import ChartPeriod from './ChartPeriod.js';
import {
	CHART_PERIOD_SELECTION_CATEGORY_KEY,
	CHART_PERIOD_SELECTION_COMPONENT_KEY,
} from './ChartPeriodSelection.meta.js';

export const NAVIGATION_KEYS = [KEYS.LEFT_ARROW, KEYS.RIGHT_ARROW, KEYS.HOME, KEYS.END];

export class ChartPeriodSelection extends ComponentBase<
	IChartPeriodSelectionProps,
	IChartPeriodSelectionState,
	IChartPeriodSelectionConfiguration
> {
	categoryKey = CHART_PERIOD_SELECTION_CATEGORY_KEY;
	componentKey = CHART_PERIOD_SELECTION_COMPONENT_KEY;
	analyticsHelper: common.helpers.AnalyticsHelper<IAppContext> | undefined;
	translationContext!: ITranslationContextValue;
	periodId = `period_${common.commonUtils.generateGuid()}`;

	focusedIndex: number | null = null;
	selectedPeriod: string | undefined = 'ALL';
	initialPeriod = 'ALL';

	currentStartDate: string | undefined;
	currentEndDate: string | undefined;

	customPeriodButton = this.props.customPeriodIndicator;
	periods = this.props.periods;
	// Will be use to link a descriptive text to the periods to describe the function of them
	describedById = common.commonUtils.generateGuid();

	constructor(props: IChartPeriodSelectionProps) {
		super(props);

		makeObservable(this, {
			focusedIndex: observable.ref,
			selectedPeriod: observable,
			initialPeriod: observable,
			isLeapDay: computed,
			setCurrentDates: action,
			setDefaultPeriod: action,
			ytdDifference: computed,
			handleClick: action,
			periodComponents: computed,
			isUsingCustomPeriod: computed,
		});

		this.setDefaultPeriod(props.defaultPeriod);
		if (props.shouldCalculateSelectedPeriod && !this.isUsingCustomPeriod) {
			this.setCurrentDates(props.currentStartDate, props.currentEndDate);
		}
	}

	get describeByText() {
		const { describeByText = "selecting this will cause the chart's data to display within this period" } =
			this.props;
		return this.translationContext.translate(describeByText);
	}

	get isLeapDay() {
		const { endDate } = this.props;
		if (endDate) {
			const date = new Date(endDate);
			return date.getMonth() === 1 && date.getDate() === 29;
		}
		return false;
	}

	componentDidUpdate(prevProps: IChartPeriodSelectionProps) {
		const { defaultPeriod, currentStartDate, currentEndDate, shouldCalculateSelectedPeriod = false } = this.props;
		if (this.initialPeriod !== defaultPeriod) {
			this.setDefaultPeriod(defaultPeriod);
		}
		if (shouldCalculateSelectedPeriod && !this.isUsingCustomPeriod) {
			if (prevProps.currentStartDate !== currentStartDate || prevProps.currentEndDate !== currentEndDate) {
				this.setCurrentDates(currentStartDate, currentEndDate);
			}
		}
	}

	getDurationFromPeriod(item: string): number {
		return Number(item.slice(0, item.length - 1));
	}

	getDurationUnitFromPeriod(item: string) {
		return item.slice(-1).toLowerCase();
	}

	setCurrentDates = (currentStartDate?: string, currentEndDate?: string) => {
		if (!currentStartDate || !currentEndDate) {
			this.currentStartDate = undefined;
			this.currentEndDate = undefined;
		} else {
			this.currentStartDate = currentStartDate;
			this.currentEndDate = currentEndDate;
			// Set the selected period
			const monthsDiff = this.getDateDifferenceInMonths(this.currentStartDate, this.currentEndDate);
			const ytdDifference = this.ytdDifference;
			this.selectedPeriod = undefined;
			if (this.props.endDate === this.currentEndDate) {
				if (this.props.startDate === this.currentStartDate) {
					this.selectedPeriod = 'ALL';
				} else {
					const { periods } = this.props;
					if (periods) {
						const displayPeriods = Object.keys(periods) as string[];
						for (const period of displayPeriods) {
							if (period !== 'ALL' && period !== 'YTD') {
								if (this.isWeekPeriod(period)) {
									const periodInWeeks = this.getDurationFromPeriod(period);
									const weekDiff = this.getDateDifferenceInWeeks(
										this.currentStartDate,
										this.currentEndDate,
									);
									if (periodInWeeks === weekDiff) {
										this.selectedPeriod = period;
										break;
									}
								} else {
									const periodInMonths = this.getPeriodMonths(period);
									if (periodInMonths === monthsDiff) {
										this.selectedPeriod = period;
										break;
									}
								}
							}
						}
					}
				}
				if (!this.selectedPeriod && monthsDiff === ytdDifference) {
					this.selectedPeriod = 'YTD';
				}
			}
		}
	};

	get isUsingCustomPeriod() {
		return this.props.customPeriodIndicator && this.props.customPeriodIndicator.enabled;
	}

	setDefaultPeriod = (defaultPeriod: string) => {
		this.initialPeriod = defaultPeriod;
		if (this.isUsingCustomPeriod && this.props.customPeriodIndicator?.selected) {
			this.selectedPeriod = '_custom';
		} else {
			this.selectedPeriod = defaultPeriod;
			this.handleClick(defaultPeriod, false);
		}
	};

	getPeriodLabel(item: string) {
		return (
			<Translate>
				{this.periodsWithCustom && this.periodsWithCustom[item] ? this.periodsWithCustom[item] : item}
			</Translate>
		);
	}

	getPeriodAriaLabel = (item: string): string | undefined => {
		// We will use the configured aria label else the displayed text
		let ariaLabel = this.periodsWithCustom && this.periodsWithCustom[item] ? this.periodsWithCustom[item] : item;
		const { periodsAriaLabel } = this.props;
		if (periodsAriaLabel) {
			ariaLabel = periodsAriaLabel[item];
		}
		return this.translationContext.translate(ariaLabel);
	};

	getPeriodMonths(item: string) {
		const durationUnit = this.getDurationUnitFromPeriod(item);
		const year = durationUnit === 'y';
		let period = this.getDurationFromPeriod(item);

		if (year) {
			period *= 12;
		}
		return period;
	}

	isWeekPeriod(item: string) {
		return this.getDurationUnitFromPeriod(item) === 'w';
	}

	isPeriodDisabled(item: string, monthDifference: number, weekDifference: number): boolean {
		if (this.isWeekPeriod(item)) {
			const weekDuration = this.getDurationFromPeriod(item);
			return weekDifference < weekDuration;
		}

		if (item !== 'ALL' && item !== 'YTD') {
			const period = this.getPeriodMonths(item);

			return monthDifference < period;
		}

		if (item === 'YTD') {
			return this.ytdDifference < 0;
		}
		return false;
	}

	getStartDateByWeeks(item: string, endDate: Date): Date {
		const weekDuration = this.getDurationFromPeriod(item) * 7;
		return new Date(endDate.setDate(endDate.getDate() - weekDuration));
	}

	get ytdDifference(): number {
		const { endDate } = this.props;
		if (endDate) {
			const date = new Date(endDate);
			// last day of previous year
			const newStartDate = `${date.getFullYear() - 1}-12-31`;

			return this.getDateDifferenceInMonths(newStartDate, endDate);
		}
		return 0;
	}

	getDateDifferenceInMonths(startDate: string, endDate: string): number {
		return common.commonUtils.getDateDifference(new Date(endDate), new Date(startDate), 'MONTHS');
	}

	getDateDifferenceInWeeks(startDate: string, endDate: string): number {
		return common.commonUtils.getDateDifference(new Date(endDate), new Date(startDate), 'WEEKS');
	}

	getNewStartDate(period: string): Date {
		const { startDate, endDate } = this.props;
		if (startDate && endDate) {
			if (period === 'ALL') {
				return new Date(startDate);
			}
			const newDate = new Date(endDate);

			if (this.isWeekPeriod(period)) {
				return this.getStartDateByWeeks(period, newDate);
			}

			const months = period === 'YTD' ? this.ytdDifference : this.getPeriodMonths(period);

			newDate.setUTCMonth(newDate.getUTCMonth() - months);

			if (this.isLeapDay && months % 12 === 0) {
				newDate.setUTCDate(newDate.getUTCDate() - 1);
			}

			return newDate;
		}
		return new Date();
	}

	handleClick = (period: string, logDataLayer = true, index?: number) => {
		const { startDate, onClick, analyticsContext, suppressAnalytics } = this.props;
		if (onClick) {
			const newStartDate = this.getNewStartDate(period);
			const dateString = newStartDate.toJSON();
			const response: IChartPeriodSelectionResponse = {
				logDataLayer,
				origStartDate: startDate || '',
				selectedStartDate: dateString.slice(0, dateString.indexOf('T')),
				timestamp: newStartDate.valueOf(),
				selectedPeriodLabel: period,
			};
			onClick(response);
			this.selectedPeriod = period;
			this.currentEndDate = this.props.endDate;
			this.currentStartDate = response.selectedStartDate;
			if (this.analyticsHelper && !suppressAnalytics) {
				let periodDefaultAnalytics = {
					period: this.selectedPeriod,
					startDate: this.currentStartDate,
					endDate: this.currentEndDate ? this.currentEndDate.replace(/T\d.*$/, '') : undefined,
				};
				if (analyticsContext) {
					periodDefaultAnalytics = { ...periodDefaultAnalytics, ...analyticsContext };
				}
				this.analyticsHelper.logEvent({
					event: 'button_click',
					eventType: 'click',
					context: periodDefaultAnalytics,
				});
			}
			if (!common.commonUtils.isNullOrUndefined(index)) {
				this.focusedIndex = index;
			}
			if (this.isUsingCustomPeriod && this.props.customPeriodIndicator?.selected) {
				this.props.customPeriodIndicator?.deselect();
			}
		}
	};

	isPeriodSelected(period: string): boolean {
		return this.selectedPeriod === period;
	}

	onNavigation(key: string) {
		// Get current focused element, in case tab moved the focus
		const currentFocusedElement = document.activeElement;
		if (currentFocusedElement) {
			const currentFocusedID = currentFocusedElement.id.split('_');
			const currFocusedIndex = Number(currentFocusedID[currentFocusedID.length - 1]);
			// Dont set the focusedIndex if it is current
			if (this.focusedIndex !== currFocusedIndex) {
				this.focusedIndex = currFocusedIndex;
			}
		} else {
			// This should never occur as this component elements would trigger this
			// the onclick will set the focus else tab would have moved the focus onto a period
			this.focusedIndex = 0;
		}

		if (this.periodComponents) {
			switch (key) {
				case KEYS.LEFT_ARROW:
					if (this.focusedIndex === 0) {
						this.focusedIndex = this.periodComponents.length - 1;
					} else {
						this.focusedIndex = this.focusedIndex - 1;
						while (
							this.periodComponents[this.focusedIndex].props.disabled === true &&
							this.focusedIndex !== 0
						) {
							this.focusedIndex--;
						}
					}
					break;
				case KEYS.RIGHT_ARROW:
					this.focusedIndex = (this.focusedIndex + 1) % this.periodComponents.length;
					while (
						this.periodComponents[this.focusedIndex].props.disabled === true &&
						this.focusedIndex <= this.periodComponents.length
					) {
						this.focusedIndex++;
					}
					break;
				case KEYS.HOME:
					this.focusedIndex = 0;
					break;
				case KEYS.END:
					this.focusedIndex = this.periodComponents.length - 1;
					break;
				default:
					break;
			}
		}
	}

	handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
		const { key } = e;
		if (NAVIGATION_KEYS.indexOf(key as KEYS) !== -1) {
			e.preventDefault();
			e.stopPropagation();
			this.onNavigation(key);
		}
	};

	get periodsWithCustom(): TPeriods | undefined {
		if (this.isUsingCustomPeriod) {
			const text = this.customPeriodButton?.text || '';
			return { ...this.periods, _custom: text };
		}
		return this.periods;
	}

	get periodComponents() {
		const { startDate, endDate } = this.props;
		const { periodsWithCustom: periods } = this;
		if (periods && startDate && endDate) {
			const displayPeriods = Object.keys(periods) as string[];
			const monthDifference = this.getDateDifferenceInMonths(startDate, endDate);
			const weekDifference = this.getDateDifferenceInWeeks(startDate, endDate);

			if (!displayPeriods || startDate > endDate) {
				return null;
			}

			return displayPeriods.map((period, index) => {
				const periodHasNoData = this.isPeriodDisabled(period, monthDifference, weekDifference);

				if (
					periodHasNoData &&
					period !== 'ALL' &&
					this.isPeriodSelected(period) &&
					!this.currentStartDate &&
					!this.currentEndDate
				) {
					this.selectedPeriod = 'ALL';
				}

				// eslint-disable-next-line @typescript-eslint/no-empty-function
				const onClick = !periodHasNoData ? () => this.handleClick(period, true, index) : () => {};

				const periodId = `${this.periodId}_button_${index}`;

				let periodProps: IChartPeriodProps = {
					onClick,
					id: periodId,
					onKeyDown: (e: any) => this.handleKeyDown(e),
					focused: !periodHasNoData && this.focusedIndex === index,
					ariaLabel: this.getPeriodAriaLabel(period),
					ariaDescribedBy: this.describedById,
				};
				// eslint-disable-next-line @typescript-eslint/naming-convention
				let PeriodComponent;
				const isCustomPeriod = period === '_custom';
				const isCustomPeriodSelected = this.props.customPeriodIndicator?.selected;
				if (isCustomPeriod) {
					// Reset the period props as the custom period should not be interactable
					periodProps = {
						id: `${this.periodId}_label_${index}`,
						ariaLabel: this.getPeriodAriaLabel(period),
						ariaDescribedBy: this.describedById,
					};
				}
				if ((isCustomPeriod && !isCustomPeriodSelected) || periodHasNoData) {
					PeriodComponent = DisabledChartPeriod;
					periodProps.disabled = true;
				} else if (
					(isCustomPeriod && isCustomPeriodSelected) ||
					(this.isPeriodSelected(period) && !isCustomPeriodSelected)
				) {
					PeriodComponent = ActiveChartPeriod;
					periodProps.active = true;
				} else {
					PeriodComponent = ChartPeriod;
				}
				return (
					<PeriodComponent key={periodId} {...periodProps}>
						{this.getPeriodLabel(period)}
					</PeriodComponent>
				);
			});
		}
	}

	render() {
		const { className } = this.props;

		/**
		 * Why is this here?
		 *
		 * The state management within Mobx is triggering a delayed render, so when we change a date in a date picker,
		 * the chart period selection component is only rendering the previous update. By referencing the this.selectedPeriod
		 * within the render, React can see that the observable has changed and triggers the correct rerender.
		 */
		this.selectedPeriod;
		const visuallyHiddenStyle = {
			position: 'absolute',
			left: '-10000px',
			top: 'auto',
			width: '1px',
			height: '1px',
			overflow: 'hidden',
		} as any;
		return (
			<TranslationContext.Consumer>
				{(translationContext: ITranslationContextValue) => {
					this.translationContext = translationContext;
					return (
						<AnalyticsContext.Consumer>
							{(analyticsHelper) => {
								this.analyticsHelper = analyticsHelper;
								return (
									<Wrapper className={className}>
										<span style={visuallyHiddenStyle} id={this.describedById}>
											{this.describeByText}
										</span>
										{this.periodComponents}
									</Wrapper>
								);
							}}
						</AnalyticsContext.Consumer>
					);
				}}
			</TranslationContext.Consumer>
		);
	}
}

export default InjectedStyledComponent(
	observer(ChartPeriodSelection),
	CHART_PERIOD_SELECTION_CATEGORY_KEY,
	CHART_PERIOD_SELECTION_COMPONENT_KEY,
);
